import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import { supabase } from './supabaseClient';
import CustomModal from './CustomModal';

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage] = useState('');


  const showModal = (title, message = '') => {
    setModalTitle(title);
    setModalOpen(true);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('tbluser')
        .select('id, firstname, lastname, middlename, username, email, role')
        .order('lastname', { ascending: true });

      if (error) throw error;

      setUsers(data || []);
    } catch (err) {
      console.error('Fetch users error:', err);
      showModal( err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const fullName = `${user.lastname || ''} ${user.firstname || ''} ${user.middlename || ''}`.toLowerCase();
      const matchesSearch =
        !search ||
        fullName.includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.username?.toLowerCase().includes(search);
      const matchesRole = !roleFilter || user.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, roleFilter]);

  return (
    <div className="h-screen overflow-y-auto px-4 py-8 mt-8">
      <div className="mx-auto max-w-6xl pt-8">
        <div className="mb-4 flex flex-col gap-2 rounded-lg border border-gray-300 bg-white p-3 shadow-sm lg:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, username, or email"
              className="w-full rounded border border-gray-300 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-sky-500 lg:w-48"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="teacher">Teacher</option>
          </select>

          <button
            type="button"
            onClick={fetchUsers}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded bg-sky-800 px-4 py-2 text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white shadow-sm">
          <table className="w-full min-w-[820px] table-auto">
            <thead className="bg-gray-200 text-left text-sm text-sky-900">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Username</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role</th>
                
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-gray-200 text-sm hover:bg-gray-50">
                    <td className="p-3 uppercase">
                      {user.lastname}, {user.firstname} {user.middlename || ''}
                    </td>
                    <td className="p-3">{user.username || '-'}</td>
                    <td className="p-3">{user.email || '-'}</td>
                    <td className="p-3 uppercase">{user.role || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
      />
    </div>
  );
}
